import { useFormContext } from "react-hook-form";
import React, { memo, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import Button from "./Buttons/Button";
import type { Image } from "../hooks/useImageUploader";
import { InspectionFormBody, Mode, Step } from "./InspectionForm";
import ImageCarousel from "./ImageCarousel";
import InspectionFormNoteModal from "./InspectionFormNoteModal";
import IconLabelButton from "./Buttons/IconLabelButton";
import InspectionFormImageModal from "./InspectionFormImageModal";
import ImageInputButton from "./Inputs/ImageInputButton";
import IconButton from "./Buttons/IconButton";
import ConfirmationModal from "./ConfimationModal";
import { getWindowHeight } from "../utils";

function InspectionFormStep({
  step,
  steps,
  images,
  notes,
  onStep,
  lastSavedTime,
  isRetry,
  onSwitchMode,
  onAddImages,
  onDeleteImage,
  onNoteSave,
  onReset,
  onChangeDescription,
}: {
  step: number;
  steps: Step[];
  images: Image[];
  notes: { [key: string]: string };
  onStep: (step: number) => void;
  lastSavedTime?: string | null;
  isRetry?: Boolean;
  saving?: boolean;
  onSave?: () => Promise<boolean | string>;
  onSwitchMode: (mode: Mode) => void;
  onAddImages: (images: FileList, key: string, isOptional?: boolean) => void;
  onDeleteImage: (key: string) => void;
  onNoteSave: (key: string, note: string) => void;
  onReset?: () => void;
  onChangeDescription: (
    images: Image[],
    key: string,
    description: string
  ) => void;
}) {
  const stepConfig = useMemo(
    () => steps.find((_, ix) => ix === step),
    [step, steps]
  );

  const [noteVisible, setNoteVisible] = useState<boolean>(false);
  const [imagesVisible, setImagesVisible] = useState<boolean>(false);
  const [confirmationModalVisible, setConfirmationModalVisible] =
    useState<boolean>(false);

  const stepImages = useMemo(
    () => images.filter((image) => image.metadata?.step === stepConfig?.key),
    [images, stepConfig]
  );

  if (!stepConfig) {
    return null;
  }

  return (
    <InspectionFormBody>
      <ButtonsContainer>
        {isRetry && (
          <Button
            label="Formulier leeg maken"
            bordered={true}
            onClick={() => setConfirmationModalVisible(true)}
          />
        )}
        <SaveButton>
          {lastSavedTime && (
            <Smalltext>
              <span>
                Laatst opgeslagen: <br /> {lastSavedTime}
              </span>
            </Smalltext>
          )}
        </SaveButton>
      </ButtonsContainer>
      <ConfirmationModal
        show={confirmationModalVisible}
        onCancel={() => setConfirmationModalVisible(false)}
        onConfirm={() => {
          if (onReset) {
            onReset();
          }
          setConfirmationModalVisible(false);
        }}
        message={
          "Weet je zeker dat je alle gegevens in dit schouwformulier wil wissen?"
        }
      />
      <StepContainer>
        <StepItem>
          <StepSection>
            <Title>{stepConfig.title}</Title>
          </StepSection>
          <StepSection>
            <>
              {stepConfig.type === "open" ? (
                <TextStep step={stepConfig} length={140} rows={2} />
              ) : null}
              {stepConfig.type === "photo" ? (
                <UploadStep
                  key={stepConfig.key}
                  step={stepConfig}
                  onAdd={(files) => onAddImages(files, stepConfig.key)}
                  onDelete={onDeleteImage}
                  onChangeDescription={onChangeDescription}
                  images={images?.filter(
                    (image) => image?.metadata?.step === stepConfig.key
                  )}
                />
              ) : null}

              {stepConfig.type === "checklist" ? (
                <ChecklistStep step={stepConfig} />
              ) : null}

              {stepConfig.type === "multipleChoice" ? (
                <ChecklistStep step={stepConfig} />
              ) : null}

              {stepConfig.type === "radio" ? (
                <RadioChoiceStep
                  step={stepConfig}
                  choices={stepConfig.choices || []}
                />
              ) : null}
              {stepConfig.type === "closed" ? (
                <RadioChoiceStep step={stepConfig} choices={["Ja", "Nee"]} />
              ) : null}
            </>
          </StepSection>
        </StepItem>

        {stepConfig.add_comment && (
          <>
            {stepConfig.key === "approval" ? (
              <StepOptionsContainer>
                <TextAreaContainer>
                  <h4>Advies bij eindoordeel</h4>
                  <Textarea
                    maxLength={400}
                    onChange={(event) =>
                      stepConfig?.key &&
                      onNoteSave(stepConfig?.key, event.target.value)
                    }
                    defaultValue={notes[stepConfig.key] || ""}
                  />
                </TextAreaContainer>
              </StepOptionsContainer>
            ) : (
              <>
                <StepOptionsContainer>
                  <IconLabelButton
                    icon="balloon"
                    label="Plaats opmerking"
                    onClick={() => setNoteVisible(true)}
                    backgroundColor="#0075BC"
                    badgeCount={notes[stepConfig.key] ? 0 : undefined}
                    iconColor="white"
                  />
                  <InspectionFormNoteModal
                    note={notes[stepConfig.key]}
                    onSave={(note: string) =>
                      stepConfig?.key && onNoteSave(stepConfig?.key, note)
                    }
                    title="Plaats opmerking"
                    submitLabel="Opslaan"
                    show={noteVisible}
                    onCancel={() => setNoteVisible(false)}
                    subtitle="Typ hier jouw opmerkingen."
                  />
                </StepOptionsContainer>
              </>
            )}
          </>
        )}
        {stepConfig.add_photo && (
          <>
            <StepOptionsContainer>
              <IconLabelButton
                icon="camera"
                label="Voeg foto toe"
                onClick={() => setImagesVisible(true)}
                backgroundColor="#0075BC"
                badgeCount={
                  stepImages.length > 0 ? stepImages.length : undefined
                }
                iconColor="white"
              />
              <InspectionFormImageModal
                images={stepImages}
                show={imagesVisible}
                step={stepConfig}
                onAdd={(files) => onAddImages(files, stepConfig.key, true)}
                onCancel={() => setImagesVisible(false)}
                onChangeDescription={onChangeDescription}
                onDeleteImage={onDeleteImage}
              />
            </StepOptionsContainer>
          </>
        )}

        <StepFooter>
          <StepButtonSection>
            {step !== steps.length - 1 ? (
              <Button
                label="Volgende"
                color="blue"
                onClick={() => onStep(step + 1)}
              />
            ) : step === steps.length - 1 ? (
              <Button
                label="Naar het overzicht van bevindingen"
                color="green"
                onClick={() => onSwitchMode(Mode.REVIEW)}
              />
            ) : null}
            {step ? (
              <Button label="Vorige" onClick={() => onStep(step - 1)} />
            ) : null}
          </StepButtonSection>
          <ProgressContainer>
            <ProgressBar steps={steps?.length}>
              {Array.from({ length: steps?.length }, (_, i) => (
                <ProgressSegment
                  key={i}
                  completed={i < step}
                  current={i === step}
                  onClick={() => onStep(i)}
                />
              ))}
            </ProgressBar>
            <ProgressText>
              Nog {steps?.length - step - 1}{" "}
              {steps?.length - step - 1 !== 1 ? "vragen" : "vraag"}
            </ProgressText>
          </ProgressContainer>
        </StepFooter>
      </StepContainer>
    </InspectionFormBody>
  );
}

const UploadStep = memo(function StepItem(props: {
  step: Step;
  images: Image[];
  onDismiss?: () => void;
  onAdd: (images: FileList) => void;
  onDelete: (key: string) => void;
  onChangeDescription: (
    images: Image[],
    key: string,
    description: string
  ) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignSelf: "center",
        alignItems: "center",
        gap: 22,
      }}
    >
      <ImageInputButton
        images={props.images}
        onAdd={props.onAdd}
        step={props.step}
      />
      <ImageCarousel
        images={props.images}
        onDelete={props.onDelete}
        onChangeDescription={props.onChangeDescription}
      />
    </div>
  );
});

function TextStep({
  step,
  length,
  rows,
}: {
  step: Step;
  length: number;
  rows: number;
}) {
  const { register } = useFormContext();
  return (
    <Textarea
      {...register(step.key, {
        required: true,
      })}
      maxLength={length}
      rows={rows}
      autoFocus={true}
    />
  );
}

function ChecklistStep({ step }: { step: Step }) {
  const { register, setValue, getValues } = useFormContext();

  const handleChoiceSelect = (index: number) => {
    const currentValue = getValues(`${step.key}.c${index}`) ?? false;
    setValue(`${step.key}.c${index}`, !currentValue);

    if (!step.add_multiple && step.type === "multipleChoice") {
      // Deselect other choices if the selected choice is true
      if (!currentValue) {
        step.choices?.forEach((_, i) => {
          if (i !== index) {
            setValue(`${step.key}.c${i}`, false);
          }
        });
      }
    }
  };

  const handleChoiceCounts = (index: number, plus: boolean) => {
    const value = getValues(`${step.key}.c${index}`) ?? 0;
    setValue(`${step.key}.c${index}`, Math.max(0, value + (plus ? 1 : -1)));
  };

  return (
    <ChoiceContainer countable={step.countable}>
      {step.choices?.map((choice, ix) => (
        <RadioContainer key={`${choice}_${ix}`}>
          <Label countableChecklist={step.countable}>
            {!step.countable && (
              <input
                type={
                  !step.add_multiple && step.type === "multipleChoice"
                    ? "radio"
                    : "checkbox"
                }
                {...register(`${step.key}.c${ix}`, {
                  required: true,
                })}
                checked={getValues(`${step.key}.c${ix}`) ?? false}
                onChange={() => handleChoiceSelect(ix)}
              />
            )}
            <Paragraph>{choice}</Paragraph>
            {step.countable && (
              <CounterContainer>
                <IconButton
                  icon="minus"
                  backgroundColor="#FAFAFA"
                  borderColor="#FAFAFA"
                  tooltip={`Verwijder ${choice}`}
                  onClick={() => handleChoiceCounts(ix, false)}
                />
                <NumberInput
                  type="number"
                  defaultValue={0}
                  {...register(`${step.key}.c${ix}`, {
                    required: true,
                    valueAsNumber: true,
                    onChange: (e) =>
                      setValue(
                        `${step.key}.c${ix}`,
                        Math.max(0, Number(e.target.value))
                      ),
                  })}
                />
                <IconButton
                  icon="plus"
                  backgroundColor="#0075BC"
                  borderColor="#0075BC"
                  iconColor="white"
                  tooltip={`Voeg ${choice} toe`}
                  onClick={() => handleChoiceCounts(ix, true)}
                />
              </CounterContainer>
            )}
          </Label>
        </RadioContainer>
      ))}
    </ChoiceContainer>
  );
}

function RadioChoiceStep({ step, choices }: { step: Step; choices: string[] }) {
  const { register } = useFormContext();
  return (
    <>
      {choices?.map((choice, ix) => (
        <RadioContainer key={`${choice}_${ix}`}>
          <Label>
            <Input
              type="radio"
              {...register(step.key, {
                required: true,
              })}
              value={choice}
            />
            <RadioBox />
            <Paragraph>{choice}</Paragraph>
          </Label>
        </RadioContainer>
      ))}
    </>
  );
}

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: ${getWindowHeight(65)};
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 30px;
  padding-bottom: 16px;
  align-self: center;
  align-content: center;
`;

const StepSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-self: center;
  align-content: center;
  text-align: center;
  min-width: 400px;
  @media only screen and (max-width: 480px) {
    min-width: unset;
    padding: 16px;
    height: 100%;
    width: 100%;
  }
`;

const StepButtonSection = styled(StepSection)`
  justify-content: center;
  height: 200px;
`;

const StepFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Title = styled.span`
  /* Tablet/H3 */
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 120%;
`;

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ProgressText = styled.span`
  align-self: flex-end;
  font-family: "Roboto";
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 120%;

  /* or 16px */
  display: flex;
  align-items: center;
  text-align: right;

  /* Donkergrijs */
  color: #212529;
`;

const ProgressBar = styled.div<{ steps: number }>`
  display: flex;
  flex-direction: row;
  gap: 10px;
  width: 100%;
  height: 10px;
  border-radius: 10px;
  overflow: hidden;
  justify-self: flex-end;
`;

const progressAnimation = css`
  transition: background-color 0.4s ease-out;
`;
const currentBarAnimation = css`
  transition: width 0.5s ease-out;
`;

const ProgressSegment = styled.div<{
  completed: boolean;
  current: boolean;
}>`
  ${({ onClick }) => (onClick ? "cursor: pointer;" : "")}
  position: relative;
  flex-grow: 1;
  flex-basis: 0;
  border-radius: 10px;

  background-color: ${({ completed, current }) =>
    completed && !current ? "#f9bf2b" : "#e5e5e5"};

  ${({ completed }) => (!completed ? progressAnimation : undefined)}

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${({ current }) => (current ? "33%" : "0%")};
    background-color: #f9bf2b;
    border-radius: 10px;

    ${({ current }) => (current ? currentBarAnimation : undefined)}
  }
`;

const RadioContainer = styled.div`
  cursor: pointer;
  width: 400px;
  background: #ffffff;
  /* Dropshadow */
  box-shadow: 0px 10px 15px rgba(168, 168, 168, 0.2);
  border-radius: 5px;
  input {
    width: 25px;
    height: 25px;
    accent-color: #0075bc;
  }
`;
const Label = styled.label<{ countableChecklist?: boolean }>`
  display: flex;
  ${({ countableChecklist }) =>
    countableChecklist
      ? "width: 95%; justify-content: space-between;"
      : "padding-left: 23px;"}
  align-items: center;
  transition: all 0.2s ease-in-out;
  &:hover {
    transform: scale(1.01);
  }
`;

const Paragraph = styled.p`
  /* Tablet/Text */
  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 120%;

  /* or 19px */
  display: flex;
  align-items: center;

  /* Donkergrijs */
  color: #212529;
  margin-left: 33px;
`;

const Textarea = styled.textarea`
  padding: 12px;

  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 120%;

  /* or 19px */
  display: flex;
  align-items: center;

  /* Donkergrijs */
  color: #212529;

  background: #ffffff;

  /* Dropshadow */
  box-shadow: 0px 10px 15px rgba(168, 168, 168, 0.2);
  border-radius: 5px;
  border: 0;
`;

const TextAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const RadioBox = styled.div`
  height: 25px;
  width: 25px;
  border: 1px solid #e5e5e5;
  background: #fafafa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  &::after {
    content: "";
    width: 55%;
    height: 55%;
    display: block;
    background: #0075bc;
    border-radius: 50%;
    transform: scale(0);
  }
`;

export const Input = styled.input`
  display: none;
  &:checked + ${RadioBox} {
    &::after {
      transform: scale(1);
    }
  }
`;
const ChoiceContainer = styled.div<{ countable?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  max-height: 400px;
  overflow: auto;
  ${({ countable }) => (countable ? "min-width: 900px;" : "")}
  @media only screen and (max-width: 1250px) {
    min-width: unset;
    padding: 16px;
    height: 100%;
    width: 100%;
  }
  padding: 20px;
`;
const CounterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 120px;
`;

const NumberInput = styled.input`
  border: none;
  width: 100%;
  text-align: center;
  &:focus {
    outline: none;
  }
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &[type="number"] {
    appearance: textfield;
    -moz-appearance: textfield; /* Firefox */
  }
`;

const StepOptionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 400px;
  padding: 12px;
  align-self: center;
`;

const Smalltext = styled.div`
  font-size: 80%;
  font-weight: 400;
  font-style: italic;
  position: relative;
  color: #62686a;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  position: relative;
  top: -30px;
`;

const SaveButton = styled.div`
  position: absolute;
  right: 0;
`;

export { UploadStep, TextStep, ChecklistStep, RadioChoiceStep };
export default InspectionFormStep;
